/**
 * Pick the product groups one member looks after. His alert mails carry only
 * these (plus alerts without a category), and the Overview and Alerts open on
 * them. Nothing ticked = all categories, which is where everyone starts.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check } from 'lucide-react';

import { Button } from '@/components/ui';
import { Modal, ModalContent, ModalHeader, ModalTitle, ModalBody, ModalFooter } from '@/components/ui/Modal';
import { toast } from '@/hooks/useToast';
import { api, formatApiError, type Category, type TeamMember } from '@/lib/api';

interface MemberCategoriesModalProps {
  member: TeamMember;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MemberCategoriesModal({ member, open, onOpenChange }: MemberCategoriesModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const categoriesQuery = useQuery({
    queryKey: ['categories'],
    queryFn: () => api.categories.list(),
    staleTime: 60_000,
    enabled: open,
  });
  const categories: Category[] = categoriesQuery.data?.items ?? [];

  // Start from what is stored each time the dialog opens, not from a
  // half-ticked list someone cancelled earlier. Keyed on the joined ids, not
  // the array: a caller that builds `member` inline hands over a new array on
  // every render, and that would wipe every tick the moment it was made.
  const stored = (member.category_ids ?? []).join(',');
  useEffect(() => {
    if (open) setSelected(new Set(stored ? stored.split(',') : []));
  }, [open, stored]);

  const saveMut = useMutation({
    mutationFn: () => api.team.setCategories(member.id, [...selected]),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['team-members'] });
      // His own list also decides where his Overview and Alerts open, and
      // what 'mine' shows there -- a cached 'mine' page would be the old one.
      void queryClient.invalidateQueries({ queryKey: ['auth-me'] });
      void queryClient.invalidateQueries({ queryKey: ['overview'] });
      void queryClient.invalidateQueries({ queryKey: ['alerts'] });
      onOpenChange(false);
      toast.success(t('settings.team.categories.saved'));
    },
    onError: (err) => toast.error(t('common.error'), { description: formatApiError(err, t('common.error')) }),
  });

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent className="w-[min(92vw,520px)]">
        <ModalHeader>
          <ModalTitle>{t('settings.team.categories.title', { name: member.name })}</ModalTitle>
        </ModalHeader>
        <ModalBody>
          <p className="text-text-secondary" style={{ fontSize: 12.5, marginBottom: 14 }}>
            {t('settings.team.categories.body')}
          </p>
          {categoriesQuery.isPending ? (
            <p className="text-text-muted" style={{ fontSize: 12.5 }}>{t('common.loading')}</p>
          ) : categories.length === 0 ? (
            <p className="text-text-muted" style={{ fontSize: 12.5 }}>{t('settings.team.categories.none')}</p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '9px 18px', maxHeight: 320, overflowY: 'auto' }}>
              {categories.map((c) => {
                const on = selected.has(c.id);
                return (
                  <label
                    key={c.id}
                    className="text-text-primary"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 9, cursor: 'pointer', userSelect: 'none', fontSize: 12.5 }}
                  >
                    <span
                      className="grid place-items-center"
                      style={{
                        width: 16,
                        height: 16,
                        borderRadius: 3,
                        flexShrink: 0,
                        border: `0.5px solid ${on ? 'var(--accent)' : 'var(--border-strong)'}`,
                        background: on ? 'var(--accent)' : 'transparent',
                      }}
                    >
                      {on && <Check className="text-on-accent" style={{ width: 10, height: 10 }} aria-hidden />}
                    </span>
                    <input type="checkbox" className="sr-only" checked={on} onChange={() => toggle(c.id)} />
                    {c.display_name || c.name}
                  </label>
                );
              })}
            </div>
          )}
          <p className="text-text-muted" style={{ fontSize: 11.5, marginTop: 14 }}>
            {selected.size === 0
              ? t('settings.team.categories.allHint')
              : t('settings.team.categories.countHint', { count: selected.size })}
          </p>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
          <Button disabled={saveMut.isPending || categoriesQuery.isPending} onClick={() => saveMut.mutate()}>
            {t('common.save')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
